import React, { useEffect, useState } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import * as moment from "moment";

import {
  TABLE_RELATED_CONTACTS_LIST,
  RELATED_CONTACT_LIST_ENDPOINT,
  FILTER_TYPE_BUTTON,
} from "@utils/constants";
import { loadAllRelatedContact } from "./RelatedContactList.actionCreators";
import { setAllRelatedContactLoadingState } from "./RelatedContactList.slice";
import EncloseTable from "@shared/baseUI/EnhancedTable/EncloseTable";
import { columnHeader, isEmpty } from "@utils/utils";
import ContactId from "./components/ContactId/ContactId";
import Communication from "./components/Communication/Communication";
import ContactAction from "./components/ContactAction/ContactAction";
import ContactJob from "./components/ContactJob/ContactJob";
import CompanyLink from "./components/CompanyLink/CompanyLink";
import enableIcon from "@icons/ic-enable.svg";
import disableIcon from "@icons/ic-disable.svg";

const PrepareDataTable = (props) => {
  const {
    t,
    isRefreshList,
    updateRefeshStatus,
    columnList,
    entityType,
    entityId,
    createRelatedContact,
    dependancies,
    countryId,
  } = props;

  const [data, setData] = useState([]);
  const [status, setStatus] = useState(2);
  const [platformUser, setPlatformUser] = useState(0);
  const [isRefreshDataAgain, setRefreshState] = useState(false);
  const [skipPageReset, setSkipPageReset] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [requestData, setRequestData] = useState({});
  const [currentPageToFirst, setCurrentPageToFirst] = useState(false);

  // const countryId = UserInfo.countryId();
  const dataURL = RELATED_CONTACT_LIST_ENDPOINT;
  const dispatch = useDispatch();
  const refreshData = (data) => {
    setRefreshState(data);
  };

  let columns = [
    {
      Header: t("ID"),
      title: "ID",
      accessor: "idInfo",
      // eslint-disable-next-line react/prop-types
      Cell: ({ cell: { value } }) => (
        <ContactId
          data={"#" + value.id}
          id={value.id}
          relatedContactId={value.relatedContactId}
          status={value.status}
          contactStatus={value.contactStatus}
          entityType={entityType}
          entityId={entityId}
          updateRefeshStatus={updateRefeshStatus}
          countryId={countryId}
        />
      ),
      disableFilters: true,
    },
    {
      Header: t("CONTACT_COMPANY"),
      title: "CONTACT_COMPANY",
      accessor: "company",
      Cell: ({ cell: { value } }) => <CompanyLink data={value} />,
    },
    {
      Header: t("CONTACT_NAME"),
      title: "CONTACT_NAME",
      accessor: "contactName",
      Cell: ({ cell: { value } }) => (
        <ContactId
          data={value.name}
          id={value.id}
          relatedContactId={value.relatedContactId}
          status={value.status}
          updateRefeshStatus={updateRefeshStatus}
        />
      ),
      disableFilters: true,
    },
    {
      Header: t("CONTACT_FUNCTION"),
      title: "CONTACT_FUNCTION",
      accessor: "jobInfo",
      Cell: ({ cell: { value } }) => (
        <ContactJob data={value.name} status={value.status} />
      ),
      disableFilters: true,
    },
    {
      Header: t("CONTACT_TYPE"),
      title: "CONTACT_TYPE",
      accessor: "rolesInfo",
      Cell: ({ cell: { value } }) => (
        <ContactJob data={value.name} status={value.status} />
      ),
      disableFilters: true,
    },
    {
      Header: t("CONTACT_COMMUNICATION"),
      title: "CONTACT_COMMUNICATION",
      accessor: "communication",
      Cell: ({ cell: { value } }) => <Communication data={value} />,
      disableFilters: true,
    },
    {
      Header: t("ACTIONS"),
      title: "ACTIONS",
      accessor: "action",
      Cell: ({ cell: { value, column } }) => (
        <ContactAction
          data={value}
          entityType={entityType}
          entityId={entityId}
          onRefreshData={refreshData}
          updateRefeshStatus={updateRefeshStatus}
          countryId={countryId}
        />
      ),
      disableFilters: true,
    },
  ];

  columns = columnHeader(columns, columnList);

  const relatedContactListByIds = useSelector(
    (state) => state.relatedContactList.byId
  );
  const relatedContactListAllIds = useSelector(
    (state) => state.relatedContactList.allIds
  );
  const filteredRecords = useSelector(
    (state) => state.relatedContactList.filteredRecords
  );

  const isRelatedContactListListLoading = useSelector(
    (state) => state.relatedContactList.isLoading
  );

  useEffect(() => {
    setLoading(isRelatedContactListListLoading);
  }, [isRelatedContactListListLoading]);

  /**
   * Method to fetch set of records based on the filters applied.
   * @param {pageSize} pageSize
   * @param {pageStart} pageStart
   * @param {globalFilter} globalFilter
   * @param {sort} sort
   * @param {appliedFilters} appliedFilters
   */
  const fetchRecords = ({
    pageSize,
    pageStart,
    globalFilter,
    sort,
    appliedFilters,
    currentPage,
  }) => {
    // save request data,
    setRequestData({
      pageSize,
      pageStart,
      globalFilter,
      sort,
      appliedFilters,
      currentPage,
      dependancies,
    });
    // Set the loading state
    setLoading(true);
    dispatch(setAllRelatedContactLoadingState());
    dispatch(
      loadAllRelatedContact({
        dataURL,
        entityId,
        entityType,
        siteId: entityId ?? "",
        pageSize,
        currentPage,
        sort,
        search: globalFilter,
        status,
        platformUser,
        dependancies,
        countryId,
      })
    );
  };

  useEffect(() => {
    if (relatedContactListAllIds.length > 0) {
      const responseData = relatedContactListAllIds.map((id) => {
        const tempData = { ...relatedContactListByIds[id] };
        tempData.idInfo = {
          id: tempData.contact.id,
          relatedContactId: tempData.id,
          status: tempData.status,
          contactStatus: tempData.contact?.status,
          entityType,
          entityId,
        };
        tempData.company = {
          id: tempData?.contact?.company?.id ?? "",
          name: tempData?.contact?.company?.name ?? t("CONTACT_COMPANY_NONE"),
          status: tempData.status,
        };
        tempData.contactName = {
          id: tempData.contact.id,
          relatedContactId: tempData.id,
          name: tempData.contact?.firstName + " " + tempData.contact?.lastName,
          status: tempData.status,
        };
        let roles = [];
        if (
          tempData.relatedContactRoles !== null &&
          tempData.relatedContactRoles !== undefined
        ) {
          roles.push(
            tempData.relatedContactRoles
              .reduce((p, c) => [...p, t(c.roleLabel)], [])
              .join(", ")
          );
        }
        tempData.rolesInfo = {
          name: roles,
          status: tempData.status,
        };
        tempData.jobInfo = {
          name: tempData.role,
          status: tempData.status,
        };

        tempData.communication = {
          email: tempData.contact?.email,
          mobilePhone: tempData.contact?.mobilePhone,
          phone: tempData.contact?.phone,
          status: tempData.status,
        };
        tempData.action = {
          id: tempData.id,
          status: tempData.status,
          contactStatus: tempData.contact?.status,
          entityType,
          entityId,
        };
        return tempData;
      });
      setData([...responseData]);
      setPageCount(filteredRecords);
      setLoading(false);
    } else {
      setData([]);
      setPageCount(0);
      setLoading(false);
    }
  }, [t, relatedContactListAllIds, relatedContactListByIds, filteredRecords]);

  useEffect(() => {
    if (isRefreshDataAgain) {
      setRefreshState(false);
    }
    if (isRefreshList) {
      setRefreshState(true);
    }
  }, [isRefreshDataAgain, isRefreshList]);

  const changeStatus = (label) => {
    if (label === "platform") {
      let platformNew = platformUser === 1 ? 0 : 1;
      setPlatformUser(platformNew);
    } else {
      let statusNew = status === 1 ? 2 : 1;
      setStatus(statusNew);
    }
  };

  useEffect(() => {
    // everyTime we apply an custom filter we reset the page to 1
    setCurrentPageToFirst(true);
    if (!isEmpty(requestData) && requestData.currentPage == 1) {
      fetchRecords({
        pageSize: requestData.pageSize,
        pageStart: requestData.pageStart,
        globalFilter: requestData.globalFilter,
        sort: requestData.sort,
        currentPage: requestData.currentPage,
        dependancies,
      });
    }
  }, [status, platformUser]);

  return (
    <EncloseTable
      type={TABLE_RELATED_CONTACTS_LIST}
      columns={columns}
      data={data}
      setData={setData}
      createNewAction={createRelatedContact}
      initialPageSize={30}
      isRefreshDataAgain={isRefreshDataAgain}
      setSkipPageReset={setSkipPageReset}
      skipPageReset={skipPageReset}
      pageCount={pageCount}
      currentPageToFirst={currentPageToFirst}
      setCurrentPageToFirst={setCurrentPageToFirst}
      setPageCount={setPageCount}
      fetchRecords={fetchRecords}
      loading={loading}
      defaultSortOptions={[{ id: "id", desc: true, dir: "desc" }]}
      extraFilters={[
        {
          type: FILTER_TYPE_BUTTON,
          label: "CONTACT_LIST_TOGGLE_ACTIVE_USERS",
          name: "users",
          labelClass: "sites-list__admins-list-label",
          containerClass: "sites-list__admins-list-dropdown-container",
          imageEnableIcon: enableIcon,
          imageDisableIcon: disableIcon,
          status,
          changeStatus: { changeStatus },
        },
        {
          type: FILTER_TYPE_BUTTON,
          label: "CONTACT_LIST_TOGGLE_PLATFORM_USERS",
          name: "platform",
          labelClass: "sites-list__admins-list-label",
          containerClass: "sites-list__admins-list-dropdown-container",
          imageEnableIcon: enableIcon,
          imageDisableIcon: disableIcon,
          status: platformUser,
          changeStatus: { changeStatus },
        },
      ]}
    />
  );
};

PrepareDataTable.propTypes = {
  t: PropTypes.func.isRequired,
  isRefreshList: PropTypes.bool.isRequired,
  columnList: PropTypes.array.isRequired,
  dependancies: PropTypes.string,
};
PrepareDataTable.defaultProps = {
  dependancies: "",
};

export default withTranslation()(PrepareDataTable);
